/**
 * 알림 & 지원 게시판 권한 정책 (실제 로그인 사용자 기준)
 *
 * - GLOW 플랫폼 관리자(ADMIN · SYSTEM): 공지/뉴스/성과·현황/자료실/매뉴얼/FAQ 작성·편집
 * - 문의하기: 회원 이상 작성 가능, 본문 편집·삭제는 본인 글 또는 GLOW 관리자
 * - 답글/댓글: 수정은 작성자 본인만, 삭제는 GLOW 관리자 포함(정책은 canDeleteSupportReply 참고)
 */

import { isGlowPlatformAdmin as checkGlowPlatformAdmin } from '../../../../assets/utils/comm/platformAdminUtils';
import {
  SECTION_NEWS,
  SECTION_PERFORMANCE,
  SECTION_SUPPORT_CENTER,
} from './supportUtils';

/** GLOW 플랫폼 관리자(ADMIN · SYSTEM) 여부 */
export function isGlowAdmin(user) {
  return checkGlowPlatformAdmin(user);
}

/** 로그인 회원 이상 (비회원 제외) */
export function isMember(user) {
  return user != null && (user.uid != null || user.id != null);
}

/** 현재 사용자 UID (문서 작성자 비교용) */
export function getCurrentUserUid(user) {
  if (!user) return null;
  const uid = user.uid ?? user.id;
  return uid != null ? Number(uid) : null;
}

/** 관리자 전용 게시판 목록 (해당 게시판은 작성/편집 모두 관리자만) */
const ADMIN_ONLY_BOARDS = {
  [SECTION_NEWS]: ['notice', 'news'],
  [SECTION_PERFORMANCE]: ['matching', 'case', 'portfolio'],
  [SECTION_SUPPORT_CENTER]: ['library', 'manual', 'faq']
};

function isAdminOnlyBoard(section, listKey) {
  const keys = ADMIN_ONLY_BOARDS[section];
  return keys && keys.includes(listKey);
}

/**
 * 게시판 글쓰기 권한
 * - 관리자 전용 게시판: GLOW 관리자만
 * - 문의하기: 회원 이상
 */
export function canWriteSupportBoard(section, listKey, user) {
  if (isAdminOnlyBoard(section, listKey)) {
    return isGlowAdmin(user);
  }
  if (section === SECTION_SUPPORT_CENTER && listKey === 'inquiry') {
    return isMember(user);
  }
  return false;
}

/**
 * 게시물 편집 권한
 * - 관리자 전용 게시판: GLOW 관리자만
 * - 문의하기: 본인 글만 (regUid === user.uid) 또는 GLOW 관리자
 */
/** 문의하기: 비활성(USE_AT≠Y) 시 일반 사용자는 본문 수정 불가 */
export function isSupportInquiryModerationBlockedForNonAdmin(user, article) {
  if (!article || isGlowAdmin(user)) return false;
  return String(article.useAt || 'Y') !== 'Y';
}

export function canEditSupportArticle(section, listKey, user, article) {
  if (isAdminOnlyBoard(section, listKey)) {
    return isGlowAdmin(user);
  }
  if (section === SECTION_SUPPORT_CENTER && listKey === 'inquiry') {
    if (isSupportInquiryModerationBlockedForNonAdmin(user, article)) return false;
    if (isGlowAdmin(user)) return true;
    const myUid = getCurrentUserUid(user);
    const authorUid = article?.regUid != null ? Number(article.regUid) : null;
    return myUid != null && authorUid != null && myUid === authorUid;
  }
  return false;
}

/**
 * 게시물 삭제 권한 — 편집 권한과 동일 (관리자 전용 게시판은 관리자만, 문의하기는 본인 글 또는 관리자)
 */
export function canDeleteSupportArticle(section, listKey, user, article) {
  return canEditSupportArticle(section, listKey, user, article);
}

/**
 * 문의하기 비밀글(OPEN_AT N) 본문·답글 열람: 작성자 또는 GLOW 관리자만.
 * @param {object|null} user AuthContext user
 * @param {{ openAt?: string, regUid?: number|string }} article 게시글(상세/목록 DTO)
 */
export function canViewSupportInquirySecretPost(user, article) {
  if (!article || article.openAt !== 'N') return true;
  if (isGlowAdmin(user)) return true;
  const myUid = getCurrentUserUid(user);
  const authorUid = article.regUid != null ? Number(article.regUid) : null;
  return myUid != null && authorUid != null && myUid === authorUid;
}

/**
 * 답글/댓글 편집 권한 — 항상 작성자 본인만 (GLOW 관리자도 타인 댓글 수정 불가)
 * - 문의하기 답글·공지/뉴스/성과 댓글 공통
 */
export function canEditSupportReply(section, listKey, user, reply) {
  const myUid = getCurrentUserUid(user);
  const replyAuthorUid = reply?.regUid != null ? Number(reply.regUid) : null;
  if (myUid == null || replyAuthorUid == null || myUid !== replyAuthorUid) return false;
  if (section === SECTION_SUPPORT_CENTER && listKey === 'inquiry') {
    return true;
  }
  if (isAdminOnlyBoard(section, listKey)) {
    return true;
  }
  return false;
}

/**
 * 답글/댓글 삭제 권한 — GLOW 관리자는 전부 삭제 가능, 그 외 본인 글은 CommentThread의 isOwnReply와 조합
 */
export function canDeleteSupportReply(section, listKey, user) {
  return isGlowAdmin(user);
}
